/* templateHeaders.js -- THE one header rule and THE one month-header reader for every onboarding template parser
 * (W-062 pass 2, 2026-09-29; Brian 09-29: one header rule for all templates, the most forgiving one).
 *
 * Before this module there were FOUR header grammars (the IS/BS parser turned only spaces into "_", the vendor and
 * prepaid parsers turned any run of non-letters/digits into "_", the employee and asset parsers compared trimmed
 * lower-case text to fixed lists) and TWO month-header rules (the IS/BS parser accepted only TEXT "YYYY-MM", so a
 * sheet whose month headers were real Excel dates imported as ZERO lines; the vendor and prepaid parsers accepted
 * dates, "YYYY-MM-DD", "Jan-23" and "MM/DD/YYYY"). Every parser now uses the rules below, which are the most
 * forgiving of those it had: a header is matched against the spec's aliases (templateSpecs.js) after
 * normalizeHeader, and a month header is read by parseHeaderMonth.
 *
 * Plain ASCII only (the template document is generated from text next to these rules).
 */

/* trim, lower-case, every run of characters that are not a-z / 0-9 becomes one "_", no leading/trailing "_".
 * "IS Category", "IS-Category", "is_category" and " is  category " all become "is_category". */
export function normalizeHeader(v) {
  return String(v == null ? '' : v).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/* A set of normalised aliases, for matching a normalised header against a spec column's `aliases`. */
export function aliasSet(aliases) {
  return new Set((aliases || []).map(normalizeHeader));
}

export const MONTH_MAP = {
  jan: '01', january: '01', feb: '02', february: '02', mar: '03', march: '03',
  apr: '04', april: '04', may: '05', jun: '06', june: '06',
  jul: '07', july: '07', aug: '08', august: '08', sep: '09', september: '09',
  oct: '10', october: '10', nov: '11', november: '11', dec: '12', december: '12',
};

export function monthNameToNum(name) {
  if (!name) return null;
  const key = String(name).trim().toLowerCase().replace(/[^a-z]/g, '');
  return MONTH_MAP[key] || null;
}

/* A month header -> "YYYY-MM", or null. Moved here unchanged from parseVendorExcel's parseHeaderDate.
 * Accepts: an Excel date cell (cell.t === 'd'), an Excel date SERIAL number (20000-60000, ~1954-2064), text
 * "YYYY-MM" / "YYYY-MM-DD" (a time part is ignored), "MM/DD/YYYY" (2-digit years -> 20xx), and "Jan-21", "Jan 21",
 * "Jan-2021", "January 2021", "Jan.21". Years outside 1990-2099 are refused. */
export function parseHeaderMonth(val, cell) {
  if (val == null) return null;
  if (cell && cell.t === 'd' && cell.v instanceof Date) {
    const d = cell.v;
    if (!isNaN(d.getTime())) {
      const yr = d.getUTCFullYear();
      const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
      if (yr >= 1990 && yr <= 2099) return `${yr}-${mo}`;
    }
  }
  if (typeof val === 'number' && val > 20000 && val < 60000) {
    const ms = (val - 25569) * 86400 * 1000;
    const d = new Date(ms);
    if (!isNaN(d.getTime())) {
      const yr = d.getUTCFullYear();
      const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
      if (yr >= 1990 && yr <= 2099) return `${yr}-${mo}`;
    }
  }
  if (typeof val === 'string') {
    const sFull = val.trim();
    const s = sFull.split(/[\sT]/)[0];
    const iso = /^(\d{4})-(\d{1,2})(?:-\d{1,2})?$/.exec(s);
    if (iso) {
      const yr = parseInt(iso[1], 10);
      const mo = iso[2].padStart(2, '0');
      if (yr >= 1990 && yr <= 2099) return `${yr}-${mo}`;
    }
    const us = /^(\d{1,2})\/\d{1,2}\/(\d{2,4})$/.exec(s);
    if (us) {
      let yr = parseInt(us[2], 10);
      if (yr < 100) yr += 2000;
      const mo = us[1].padStart(2, '0');
      if (yr >= 1990 && yr <= 2099) return `${yr}-${mo}`;
    }
    const mName = /^([A-Za-z]+)[\s\-./]+(\d{2,4})$/.exec(sFull);
    if (mName) {
      const mo = monthNameToNum(mName[1]);
      let yr = parseInt(mName[2], 10);
      if (yr < 100) yr += 2000;
      if (mo && yr >= 1990 && yr <= 2099) return `${yr}-${mo}`;
    }
  }
  return null;
}

/* A row name that LOOKS LIKE A TOTAL: the word "total" or "subtotal" anywhere in it (W-068 pass 3). ONE predicate for
 * the template check (F1: such a row is KEPT and flagged, never dropped), Step 3 (it is pre-set to Skip and marked
 * "suggested -- looks like a total row -- confirm or change") and the Step-4 payload + review bridge (a row the operator
 * then maps to a real section is booked like any other line). It is the same test the parsers' own isTotalRow /
 * isTotalLabel apply; those remain only on the LEGACY layouts, which still drop such rows as before. */
export function looksLikeTotalRow(name) {
  if (name == null) return false;
  return /\b(total|subtotal)\b/i.test(String(name));
}
